import type { CSSProperties, MouseEvent } from 'react';
import { Fingerprint, KeyRound, Lock, MoreVertical, ShieldCheck, Trash2 } from 'lucide-react';
import type { SavedAccount } from '../../auth/savedAccounts';
import { UserAvatar } from '../../components/ui/UserAvatar';

export interface SavedAccountCardProps {
  account: SavedAccount;
  isMobile: boolean;
  desktopScale: number;
  cardWidth: number | string;
  cardHeight: number | string;
  isHovered: boolean;
  isMenuOpen: boolean;
  onMouseEnter: () => void;
  onMouseLeave: () => void;
  onClick: () => void;
  onToggleMenu: (e: MouseEvent<HTMLButtonElement>) => void;
  isPasskeyEnabled: boolean;
  handleGoogleLogin: () => void;
  handlePasskeyLogin: (email: string) => void;
  openEmailLogin: (email: string, isFromSaved?: boolean) => void;
  verifyPasskeyBeforeAction: (email: string, callback: () => void, action?: 'desvincular' | 'remover') => void | Promise<void>;
  clearLocalPasskeyFlag: (email: string) => void;
  removeSavedAccount: (id: string) => void;
  onCloseMenu?: () => void;
}

function formatProfileName(fullName: string): string {
  if (!fullName) return '';
  const parts = fullName.trim().split(/\s+/);
  if (parts.length <= 1) return fullName;

  const firstName = parts[0];
  const prepositions = new Set(['de', 'da', 'do', 'dos', 'das', 'e']);
  const abbreviatedSurnames = parts.slice(1)
    .filter(part => !prepositions.has(part.toLowerCase()))
    .map(part => part.charAt(0).toUpperCase() + '.');

  if (abbreviatedSurnames.length === 0) return firstName;
  return `${firstName} ${abbreviatedSurnames.join(' ')}`;
}

function obfuscateEmail(email: string): string {
  if (!email) return '';
  const [localPart, domain] = email.split('@');
  if (!domain) return email;

  if (localPart.length <= 2) {
    return `${localPart.charAt(0)}*@${domain}`;
  }

  const visibleStart = localPart.slice(0, 2);
  const visibleEnd = localPart.slice(-1);
  const asterisks = '*'.repeat(Math.max(3, localPart.length - 3));
  return `${visibleStart}${asterisks}${visibleEnd}@${domain}`;
}

export function SavedAccountCard({
  account,
  isMobile,
  desktopScale,
  cardWidth,
  cardHeight,
  isHovered,
  isMenuOpen,
  onMouseEnter,
  onMouseLeave,
  onClick,
  onToggleMenu,
  isPasskeyEnabled,
  handleGoogleLogin,
  handlePasskeyLogin,
  openEmailLogin,
  verifyPasskeyBeforeAction,
  clearLocalPasskeyFlag,
  removeSavedAccount,
  onCloseMenu,
}: SavedAccountCardProps) {
  const isPendingSecurity = account.localStatus === 'pending_security';

  // Responsive sizes based on layout metrics
  const avatarSize = isMobile ? 118 : Math.round(Math.min(130 * desktopScale, 150));
  const avatarFrameSize = isMobile ? 136 : Math.round(Math.min(156 * desktopScale, 180));
  const menuIconSize = isMobile ? 16 : Math.round(16 * desktopScale);
  const passkeyBadgeIconSize = isMobile ? 36 : Math.round(28 * desktopScale);
  const roleBadgePadding = isMobile
    ? '3px 7px'
    : `${Math.round(3 * desktopScale)}px ${Math.round(7 * desktopScale)}px`;
  const roleBadgeFontSize = isMobile
    ? '0.94rem'
    : `${(0.92 * Math.min(desktopScale, 1.25)).toFixed(2)}rem`;
  const profileNameFontSize = isMobile
    ? '1.24rem'
    : `${(1.30 * Math.min(desktopScale, 1.35)).toFixed(2)}rem`;
  const profileEmailFontSize = isMobile
    ? '0.6rem'
    : `${(0.66 * Math.min(desktopScale, 1.2)).toFixed(2)}rem`;

  return (
    <div
      style={{
        flexShrink: 0,
        position: 'relative',
        width: cardWidth,
        height: cardHeight,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: isMobile ? 6 : Math.round(8 * desktopScale),
        cursor: 'pointer',
        opacity: 1,
        filter: 'none',
        transition: 'all 0.3s cubic-bezier(0.25, 0.8, 0.25, 1)',
        padding: isMobile
          ? '6px 10px 22px'
          : `${Math.max(6, Math.round(8 * desktopScale))}px ${Math.round(12 * desktopScale)}px ${Math.round(28 * desktopScale)}px`,
        borderRadius: 16,
        overflow: 'visible',
        background: isMobile
          ? isPendingSecurity
            ? 'linear-gradient(160deg, rgba(117, 68, 230, 0.38), rgba(139, 92, 246, 0.24))'
            : 'linear-gradient(160deg, rgba(112, 55, 214, 0.78), rgba(139, 92, 246, 0.48))'
          : isHovered
            ? 'linear-gradient(155deg, rgba(78,31,150,0.92), rgba(27,9,57,0.94))'
            : isPendingSecurity
              ? 'linear-gradient(155deg, rgba(78,31,150,0.42), rgba(27,9,57,0.54))'
              : 'linear-gradient(155deg, rgba(78,31,150,0.72), rgba(27,9,57,0.78))',
        backdropFilter: 'blur(20px)',
        border: isPendingSecurity ? '1.5px dashed' : '1.5px solid',
        borderColor: isHovered ? 'rgba(196, 181, 253, 0.9)' : 'rgba(221, 214, 254, 0.18)',
        boxShadow: isHovered
          ? '0 0 30px rgba(139, 92, 246, 0.50), 0 22px 44px rgba(17, 7, 45, 0.34)'
          : '0 14px 32px rgba(17,7,45,0.18)',
        transform: isHovered ? 'translateY(-10px)' : 'none',
        zIndex: isMenuOpen ? 50 : (isHovered ? 10 : 1),
      }}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      onClick={onClick}
    >
      {/* Top Header Row containing Role badge and Dropdown/Ellipsis Menu button */}
      <div
        style={{
          width: '100%',
          marginLeft: isMobile ? -8 : -Math.round(10 * desktopScale),
          marginRight: isMobile ? -3 : -Math.round(4 * desktopScale),
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'flex-start',
          gap: isMobile ? 0 : Math.round(2 * desktopScale),
          minHeight: isMobile ? 22 : Math.round(24 * desktopScale),
        }}
      >
        <button
          type="button"
          onClick={onToggleMenu}
          style={{
            flex: '0 0 auto',
            background: 'transparent',
            border: 'none',
            borderRadius: 0,
            width: isMobile ? 22 : Math.round(22 * desktopScale),
            height: isMobile ? 22 : Math.round(22 * desktopScale),
            padding: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            boxShadow: 'none',
            color: '#ffffff',
            opacity: 0.7,
            transition: 'all 0.2s',
            zIndex: 6,
            marginRight: isMobile ? -4 : -Math.round(6 * desktopScale), // Pull the role text closer
          }}
        >
          <MoreVertical size={menuIconSize} />
        </button>
        <div
          style={{
            display: 'flex',
            flexDirection: 'row',
            flexWrap: 'nowrap',
            alignItems: 'center',
            justifyContent: 'flex-start',
            gap: isMobile ? 4 : Math.round(6 * desktopScale),
            minWidth: 0,
            paddingRight: 0,
            boxSizing: 'border-box',
          }}
        >
          <span
            style={{
              display: 'inline-block',
              padding: roleBadgePadding,
              paddingLeft: 0, // Eliminate transparent left padding to align with dots menu
              borderRadius: isMobile ? 6 : Math.round(7 * desktopScale),
              background: 'transparent',
              color: 'rgba(255, 255, 255, 0.7)',
              opacity: 0.7,
              fontSize: roleBadgeFontSize,
              fontWeight: 500,
              textTransform: 'uppercase',
              letterSpacing: '0.03em',
              border: 'none',
              boxShadow: 'none',
              maxWidth: '100%',
              minWidth: 0,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {(() => {
              let cargo = account.cargo;

              // Smart fallback for existing accounts in localStorage that don't have the cargo field yet
              if (!cargo && account.role === 'ESCOLA') {
                const nameLower = (account.name || '').toLowerCase();
                const emailLower = (account.email || '').toLowerCase();
                if (nameLower.includes('diretor') || emailLower.includes('d1')) {
                  cargo = 'DIRETOR';
                } else if (nameLower.includes('secretario') || nameLower.includes('secretaria') || emailLower.includes('s1')) {
                  cargo = 'SECRETARIA';
                } else if (nameLower.includes('coord') || emailLower.includes('c1')) {
                  cargo = 'COORDENACAO';
                }
              }

              if (account.role === 'ESCOLA' && cargo) {
                const c = cargo.toUpperCase();
                if (c === 'DIRETOR') return 'DIRETOR';
                if (c === 'COORDENACAO' || c === 'COORDENADORA' || c === 'COORDENADOR') return 'COORDENADOR';
                if (c === 'SECRETARIA' || c === 'SECRETARIO' || c === 'SECRETÁRIO') return 'SECRETÁRIO';
                return c;
              }
              return account.role;
            })()}
          </span>
        </div>
      </div>

      {/* Passkey floating icon at the top right */}
      {account.hasPasskey && isPasskeyEnabled && (
        <div
          style={{
            position: 'absolute',
            top: isMobile ? 10 : Math.round(12 * desktopScale),
            right: isMobile ? 12 : Math.round(14 * desktopScale),
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 5,
          }}
          title="Biometria cadastrada"
        >
          {/* Gold metallic gradient definition */}
          <svg width="0" height="0" style={{ position: 'absolute' }}>
            <defs>
              <linearGradient id="gold-key-gradient" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" stopColor="#fff2a3" />
                <stop offset="30%" stopColor="#ffd700" />
                <stop offset="70%" stopColor="#ca9b00" />
                <stop offset="100%" stopColor="#8a6400" />
              </linearGradient>
              <linearGradient id="gold-key-border-gradient" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" stopColor="#73560f" />
                <stop offset="50%" stopColor="#473200" />
                <stop offset="100%" stopColor="#241800" />
              </linearGradient>
            </defs>
          </svg>
          <KeyRound
            size={isMobile ? 38 : Math.round(32 * desktopScale)}
            strokeWidth={2}
            color="url(#gold-key-border-gradient)"
            fill="url(#gold-key-gradient)"
            style={{
              filter: 'drop-shadow(0 2px 8px rgba(253, 208, 67, 0.45))',
            }}
          />
        </div>
      )}

      {/* Avatar Image Frame & Badges */}
      <div
        style={{
          position: 'relative',
          width: avatarFrameSize,
          height: avatarFrameSize,
          borderRadius: '50%',
          padding: isMobile ? 5 : Math.max(5, Math.round(6 * desktopScale)),
          boxSizing: 'border-box',
          background: 'linear-gradient(145deg, rgba(255,255,255,0.28), rgba(167,139,250,0.16))',
          boxShadow: '0 16px 34px rgba(17, 7, 45, 0.32)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <UserAvatar
          src={account.avatar}
          name={account.name}
          role={account.role}
          size={avatarSize}
        />

        {/* Google badge */}
        {account.authMethod === 'google' && (
          <div
            style={{
              position: 'absolute',
              top: -2,
              right: -2,
              background: '#fff',
              borderRadius: '50%',
              padding: 3,
              display: 'flex',
              boxShadow: '0 2px 6px rgba(0,0,0,0.25)',
            }}
          >
            <svg width="12" height="12" viewBox="0 0 24 24">
              <path
                fill="#4285F4"
                d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
              />
              <path
                fill="#34A853"
                d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
              />
              <path
                fill="#FBBC05"
                d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
              />
              <path
                fill="#EA4335"
                d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
              />
            </svg>
          </div>
        )}

      </div>

      {/* Profile Name & Email */}
      <div
        style={{
          textAlign: 'center',
          width: '100%',
          minWidth: 0,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: isMobile ? 3 : Math.round(4 * desktopScale),
          marginTop: isMobile ? 5 : Math.round(5 * desktopScale),
          marginBottom: isMobile ? -5 : -Math.round(5 * desktopScale),
        }}
      >
        <div
          style={{
            color: '#ffffff',
            fontWeight: 500,
            fontSize: profileNameFontSize,
            lineHeight: 1.08,
            maxWidth: '100%',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {formatProfileName(account.name)}
        </div>
      </div>

      {/* Hidden legacy badges layout placeholder */}
      <div style={{ display: 'none' }} />

      {/* Dropdown Options Menu */}
      {isMenuOpen && (
        <div
          style={{
            position: 'absolute',
            top: 48,
            left: 12,
            width: 220,
            background: '#0f0a1a',
            border: '1px solid rgba(167, 139, 250, 0.25)',
            borderRadius: 12,
            boxShadow: '0 10px 30px rgba(0, 0, 0, 0.5)',
            zIndex: 10,
            padding: 6,
            animation: 'acm-fadeIn 0.15s ease',
            backdropFilter: 'blur(10px)',
          }}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Email info */}
          <div
            style={{
              padding: '8px 12px',
              fontSize: '0.72rem',
              color: '#94a3b8',
              borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
              marginBottom: 4,
              userSelect: 'text',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {obfuscateEmail(account.email)}
          </div>

          {account.hasPasskey && isPasskeyEnabled && (
            <button
              type="button"
              onClick={() => {
                verifyPasskeyBeforeAction(
                  account.email,
                  () => {
                    clearLocalPasskeyFlag(account.email);
                    onCloseMenu?.();
                  },
                  'desvincular'
                );
              }}
              style={{
                width: '100%',
                display: 'flex',
                alignItems: 'center',
                gap: 10,
                padding: '10px 12px',
                fontSize: '0.8rem',
                color: '#e2e8f0',
                background: 'transparent',
                border: 'none',
                cursor: 'pointer',
                borderRadius: 6,
                textAlign: 'left',
                fontWeight: 500,
                transition: 'background 0.2s',
              }}
              onMouseEnter={(e) => (e.currentTarget.style.background = 'rgba(255, 255, 255, 0.05)')}
              onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
            >
              <ShieldCheck size={14} style={{ color: '#10b981' }} /> Desvincular biometria
            </button>
          )}

          <button
            type="button"
            onClick={() => {
              if (account.hasPasskey && isPasskeyEnabled) {
                verifyPasskeyBeforeAction(
                  account.email,
                  () => {
                    removeSavedAccount(account.id);
                  },
                  'remover'
                );
              } else {
                removeSavedAccount(account.id);
              }
            }}
            style={{
              width: '100%',
              display: 'flex',
              alignItems: 'center',
              gap: 10,
              padding: '10px 12px',
              fontSize: '0.8rem',
              color: '#f87171',
              background: 'transparent',
              border: 'none',
              cursor: 'pointer',
              borderRadius: 6,
              textAlign: 'left',
              fontWeight: 500,
              transition: 'background 0.2s',
            }}
            onMouseEnter={(e) => (e.currentTarget.style.background = 'rgba(239, 68, 68, 0.08)')}
            onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
          >
            <Trash2 size={14} /> Remover perfil
          </button>
        </div>
      )}
    </div>
  );
}
